import type MapboxDraw from '@mapbox/mapbox-gl-draw';
import type { Feature, FeatureCollection, GeoJsonProperties, Geometry, Polygon as GJPolygon } from 'geojson';
import { ensureClosed, enforceLeftHandRings, orientRing } from './map-geometry.utils';

type Ring = [number, number][];

export function allPolygons(draw: MapboxDraw): Feature<GJPolygon>[] {
  const fc = draw.getAll() as FeatureCollection<Geometry, GeoJsonProperties>;
  return (fc.features || []).filter((f) => f?.geometry?.type === 'Polygon') as Feature<GJPolygon>[];
}

export function siteGeometry(draw: MapboxDraw | undefined): FeatureCollection {
  const features = ((draw?.getAll() as FeatureCollection | undefined)?.features ?? []).filter(
    (f) => f?.geometry?.type === 'Polygon' || f?.geometry?.type === 'Point'
  );
  return { type: 'FeatureCollection', features };
}

export function selectedIds(draw: MapboxDraw): string[] {
  try {
    return (draw as any).getSelectedIds?.() ?? [];
  } catch {
    return [];
  }
}

function firstId(added: string | string[]): string {
  return Array.isArray(added) ? added[0] : added;
}

function ringsDiffer(before: Ring[], after: Ring[]): boolean {
  return (
    before.length !== after.length ||
    before.some(
      (ring, i) =>
        ring.length !== after[i].length || ring.some((p, j) => p[0] !== after[i][j][0] || p[1] !== after[i][j][1])
    )
  );
}

export function enforceLeftHandRules(draw: MapboxDraw, onError: (error: unknown) => void): void {
  const updates: Array<{ id: string; coords: Ring[] }> = [];
  for (const f of allPolygons(draw)) {
    const before = (f.geometry.coordinates ?? []) as Ring[];
    const oriented = enforceLeftHandRings(before);
    if (ringsDiffer(before, oriented)) updates.push({ id: (f as any).id as string, coords: oriented });
  }
  for (const update of updates) {
    const old = draw.get(update.id) as Feature<GJPolygon> | undefined;
    if (!old) continue;
    try {
      draw.delete(update.id);
      const replacement: Feature<GJPolygon> = {
        type: 'Feature',
        properties: { ...(old.properties ?? {}) },
        geometry: { type: 'Polygon', coordinates: update.coords },
      };
      const nextId = firstId((draw as any).add(replacement));
      try {
        (draw as any).changeMode('direct_select', { featureId: nextId });
      } catch {}
    } catch (err) {
      onError(err);
    }
  }
}

export function replacePolygonRing(
  draw: MapboxDraw,
  id: string,
  newRing: Ring,
  properties?: Record<string, any>
): string | null {
  const existing = draw.get(id) as any;
  if (!existing) return null;
  const closed = ensureClosed(newRing);
  if (closed.length < 4) return null;
  const holes = (existing.geometry?.coordinates?.slice(1) ?? []) as Ring[];

  draw.delete(id);
  const replacement: Feature<GJPolygon> = {
    type: 'Feature',
    properties: { ...(existing.properties ?? {}), ...(properties ?? {}) },
    geometry: { type: 'Polygon', coordinates: [orientRing(closed, true), ...holes.map((h) => orientRing(h, false))] },
  };
  const nextId = firstId((draw as any).add(replacement));
  selectReplacement(draw, nextId, Boolean((replacement.properties as any)?._circle));
  return nextId;
}

function selectReplacement(draw: MapboxDraw, id: string, isCircle: boolean): void {
  try {
    if (isCircle) (draw as any).changeMode('simple_select', { featureIds: [id] });
    else (draw as any).changeMode('direct_select', { featureId: id });
  } catch {}
}
